import React from 'react';

import { GGJLogoFull } from '../Brand/GGJLogo';

import AuthPinHeroPanel from './AuthPinHeroPanel';
import AuthPinPlatformCarousel from './AuthPinPlatformCarousel';

export interface AuthPinShellProps {
  t: (key: string) => string;
  onBackToHome: () => void;
  eyebrowKey?: string;
  titleBeforeKey?: string;
  titleAccentKey?: string;
  subtitleKey?: string;
  bodyKey?: string;
  footerStatKey?: string;
  platformsLabelKey?: string;
  cardHeadingKey: string;
  cardSubtitleKey: string;
  cardIntroKey?: string;
  cardNote?: React.ReactNode;
  formPanelEyebrowKey?: string;
  formPanelTopKey?: string;
  formPanelBottomKey?: string;
  children: React.ReactNode;
  cardFooter?: React.ReactNode;
  layout?: 'form-left' | 'form-right' | 'mirrored';
  proofMode?: 'copy' | 'hero' | 'pin';
  /** Full-screen spinner while OAuth completes and app navigates to /agent */
  redirecting?: boolean;
}

const AuthPinShell: React.FC<AuthPinShellProps> = ({
  t,
  onBackToHome,
  eyebrowKey = 'auth.loginLeftEyebrow',
  titleBeforeKey,
  titleAccentKey,
  subtitleKey,
  bodyKey,
  footerStatKey = 'auth.pinFooterStat',
  platformsLabelKey = 'auth.loginPlatformsLabel',
  cardHeadingKey,
  cardSubtitleKey,
  cardIntroKey,
  cardNote,
  formPanelEyebrowKey,
  formPanelTopKey,
  formPanelBottomKey,
  children,
  cardFooter,
  layout = 'form-right',
  proofMode = 'copy',
  redirecting = false,
}) => {
  const layoutClass =
    layout === 'form-left'
      ? 'auth-pin-login--form-left'
      : layout === 'mirrored'
        ? 'auth-pin-login--mirrored'
        : 'auth-pin-login--form-right';
  const isHero = proofMode === 'hero';
  const isPin = proofMode === 'pin';
  const splitHeroClass = isHero ? 'auth-pin-login--split-hero auth-container' : '';

  const formPanelTop =
    formPanelEyebrowKey || formPanelTopKey ? (
      <div className="auth-pin-login__form-panel-top">
        {formPanelEyebrowKey ? (
          <p className="hiw__eyebrow auth-pin-login__eyebrow auth-pin-login__form-panel-eyebrow text-balance">
            {t(formPanelEyebrowKey)}
          </p>
        ) : null}
        {formPanelTopKey ? (
          <p className="auth-pin-login__body auth-pin-login__form-panel-note auth-pin-login__form-panel-note--top text-balance">
            {t(formPanelTopKey)}
          </p>
        ) : null}
      </div>
    ) : null;

  const formCardNote =
    cardNote ??
    (cardIntroKey ? (
      <p className="auth-pin-login__body auth-pin-login__card-note text-balance">{t(cardIntroKey)}</p>
    ) : null);

  const formCard = (
    <div className="auth-pin-login__card">
      <div className="auth-pin-login__card-head">
        <h1 className="hiw__title text-balance auth-pin-login__headline auth-pin-login__card-title">
          {t(cardHeadingKey)}
        </h1>
        <p className="hiw__subtitle text-balance auth-pin-login__subhead auth-pin-login__card-subtitle">
          {t(cardSubtitleKey)}
        </p>
      </div>

      <div className="auth-pin-login__card-body">{children}</div>

      {!isPin ? formCardNote : null}

      {cardFooter ? <div className="auth-pin-login__card-footer">{cardFooter}</div> : null}
    </div>
  );

  const formPanelMiddle = isPin && formCardNote ? (
    <div className="auth-pin-login__form-panel-middle">{formCardNote}</div>
  ) : null;

  const formPanelBottom = formPanelBottomKey ? (
    <p className="auth-pin-login__body auth-pin-login__form-panel-note auth-pin-login__form-panel-note--bottom text-balance">
      {t(formPanelBottomKey)}
    </p>
  ) : null;

  const formColumnContent = isPin ? (
    <div className="auth-pin-login__signin-shell">
      {formPanelTop}
      {formCard}
      {formPanelMiddle}
      {formPanelBottom}
    </div>
  ) : (
    <>
      {formPanelTop}
      {formCard}
      {formPanelBottom}
    </>
  );

  const formColumn = (
    <section
      className={isHero || isPin ? 'auth-form-panel auth-pin-login__form-col' : 'auth-pin-login__form-col'}
      aria-label={t(cardHeadingKey)}
    >
      {formColumnContent}
    </section>
  );

  const pinProofColumn = (
    <section className="auth-pin-login__proof-col auth-pin-login__proof-col--pin" aria-label={t(eyebrowKey)}>
      <div className="auth-pin-login__proof-inner auth-pin-login__proof-inner--pin">
        <div className="auth-pin-login__proof-visual">
          <div className="auth-pin-login__hero-frame--pin">
            <AuthPinHeroPanel t={t} />
            <div className="auth-pin-login__hero-copy-overlay">
              <div className="auth-pin-login__hero-copy-main">
                <p className="hiw__eyebrow auth-pin-login__eyebrow auth-pin-login__hero-eyebrow--pin">
                  {t(eyebrowKey)}
                </p>
                {titleBeforeKey && titleAccentKey ? (
                  <h2 className="hiw__title text-balance auth-pin-login__headline auth-pin-login__hero-headline--pin">
                    {t(titleBeforeKey)}{' '}
                    <span className="hiw__title-accent">{t(titleAccentKey)}</span>
                  </h2>
                ) : null}
                {subtitleKey ? (
                  <p className="hiw__subtitle text-balance auth-pin-login__hero-subhead--pin">
                    {t(subtitleKey)}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="auth-pin-login__hero-bottom-overlay">
              {footerStatKey ? (
                <p className="hiw__subtitle text-balance auth-pin-login__stat auth-pin-login__stat--pin-hero">
                  {t(footerStatKey)}
                </p>
              ) : null}
              <div className="auth-pin-login__hero-bottom-platforms">
                <p className="auth-pin-login__platforms-label">{t(platformsLabelKey)}</p>
                <AuthPinPlatformCarousel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const proofColumn =
    proofMode === 'hero' ? (
      <section className="auth-visual-panel auth-pin-login__proof-col--hero" aria-label={t(eyebrowKey)}>
        <AuthPinHeroPanel t={t} />
      </section>
    ) : proofMode === 'pin' ? (
      pinProofColumn
    ) : (
      <section className="auth-pin-login__proof-col" aria-label={t(eyebrowKey)}>
        <div className="auth-pin-login__proof-glow" aria-hidden />
        <div className="auth-pin-login__proof-inner">
          <div className="auth-pin-login__proof-main auth-pin-login__proof-block">
            <header className="auth-pin-login__proof-header">
              <p className="hiw__eyebrow auth-pin-login__eyebrow">{t(eyebrowKey)}</p>
              {titleBeforeKey && titleAccentKey ? (
                <h2 className="hiw__title text-balance auth-pin-login__headline">
                  {t(titleBeforeKey)}{' '}
                  <span className="hiw__title-accent">{t(titleAccentKey)}</span>
                </h2>
              ) : null}
              {(subtitleKey || bodyKey) && (
                <div className="auth-pin-login__proof-copy">
                  {subtitleKey ? (
                    <p className="hiw__subtitle text-balance auth-pin-login__subhead">
                      {t(subtitleKey)}
                    </p>
                  ) : null}
                  {bodyKey ? (
                    <p className="auth-pin-login__body text-balance">{t(bodyKey)}</p>
                  ) : null}
                </div>
              )}
            </header>
          </div>

          <div className="auth-pin-login__proof-footer auth-pin-login__proof-block">
            <p className="auth-pin-login__platforms-label">{t(platformsLabelKey)}</p>
            <AuthPinPlatformCarousel />
            {footerStatKey ? (
              <p className="hiw__subtitle text-balance auth-pin-login__stat">{t(footerStatKey)}</p>
            ) : null}
          </div>
        </div>
      </section>
    );

  const brandLockup = (
    <button type="button" onClick={onBackToHome} className="auth-brand-overlay__btn">
      <GGJLogoFull
        iconSize={26}
        fontSize={18}
        textColor="#111827"
        className="auth-pin-login__brand-lockup auth-brand-overlay__lockup"
      />
    </button>
  );

  if (isHero) {
    return (
      <div
        className={`auth-pin-login auth-page auth-container overflow-hidden ${layoutClass} ${splitHeroClass}`.trim()}
      >
        <div className="auth-brand-overlay">{brandLockup}</div>
        {layout === 'form-left' || layout === 'mirrored' ? (
          <>
            {formColumn}
            {proofColumn}
          </>
        ) : (
          <>
            {proofColumn}
            {formColumn}
          </>
        )}
      </div>
    );
  }

  if (isPin) {
    return (
      <div
        className={`auth-pin-login auth-page auth-pin-login--pin-split min-h-dvh flex flex-col md:h-dvh md:max-h-dvh md:overflow-hidden ${layoutClass}`.trim()}
      >
        <header className="auth-pin-login__header auth-pin-login__header--pin">
          <button type="button" onClick={onBackToHome} className="auth-pin-login__logo-btn">
            <GGJLogoFull iconSize={26} fontSize={18} className="auth-pin-login__brand-lockup" />
          </button>
        </header>

        <div className="auth-pin-login__pin-body flex-1 min-h-0 overflow-y-auto overflow-x-hidden lg:overflow-hidden">
          {layout === 'form-left' ? (
            <>
              {formColumn}
              {proofColumn}
            </>
          ) : (
            <>
              {proofColumn}
              {formColumn}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`auth-pin-login auth-page h-dvh max-h-dvh overflow-hidden flex flex-col ${layoutClass}`.trim()}
    >
      <header className="auth-pin-login__header">
        <button type="button" onClick={onBackToHome} className="auth-pin-login__logo-btn">
          <GGJLogoFull iconSize={30} fontSize={16} className="auth-pin-login__brand-lockup" />
        </button>
      </header>

      <div className="auth-pin-login__layout flex-1 min-h-0 overflow-hidden">
        {layout === 'form-left' ? (
          <>
            {formColumn}
            {proofColumn}
          </>
        ) : (
          <>
            {proofColumn}
            {formColumn}
          </>
        )}
      </div>

      {redirecting && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-[var(--vscode-bg-primary,#0f172a)]/90 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-label={t('auth.connecting')}
        >
          <div className="w-10 h-10 rounded-full border-2 border-[#6C47FF]/20 border-t-[#6C47FF] animate-spin" />
        </div>
      )}
    </div>
  );
};

export default AuthPinShell;
